/**
 * PaywallPaymentForm - Payment form with Stripe Elements
 *
 * Reuses the same Stripe components as the payment page:
 * - StripePaymentElement for card input
 * - StripeExpressCheckoutElement for Apple Pay/Google Pay (create_subscription flow only)
 * - ZipCodeInputV2 for postal code
 *
 * Supports two flows:
 * - update_payment_method: SetupIntent flow (card only, no Express Checkout)
 * - create_subscription: PaymentIntent flow with Express Checkout
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import StripePaymentElement from '@/app/components/payment/components/method/components/stripe-payment-element';
import StripeExpressCheckoutElement from '@/app/components/payment/components/method/components/stripe-express-checkout-element';
import ZipCodeInputV2 from '@/app/components/payment/components/method/components/zip-code-input-v2';
import type { PaymentUpdateState } from '../useUpdatePaymentMethod';
import type { SubscriptionCreateState } from '../useCreateSubscription';
import type { PaywallFlowType } from '../usePaywallDetection';
import type { StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';
import '../paywall-reset.css';

// Combined state type for both flows
type PaywallState = PaymentUpdateState | SubscriptionCreateState;

interface PaywallPaymentFormProps {
  state: PaywallState;
  onSubmit: (zipCode?: string) => Promise<void>;
  flowType: PaywallFlowType | null;
  priceAmount?: number;
  onExpressCheckout?: (event: StripeExpressCheckoutElementConfirmEvent) => Promise<void>;
}

interface FormData {
  email: string;
  name: string;
  autoRenewal: boolean;
  coupon: string;
  zip: string;
}

export function PaywallPaymentForm({
  state,
  onSubmit,
  flowType,
  priceAmount,
  onExpressCheckout,
}: PaywallPaymentFormProps) {
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [showExpressCheckout, setShowExpressCheckout] = useState(true);

  const form = useForm<FormData>({
    defaultValues: {
      email: '',
      name: '',
      autoRenewal: true,
      coupon: '',
      zip: '',
    },
  });

  const isSubmitting = state !== 'idle' && state !== 'error';
  const isCreateSubscriptionFlow = flowType === 'create_subscription';

  const onFormSubmit = form.handleSubmit(async (data) => {
    await onSubmit(data.zip);
  });

  const getButtonText = () => {
    switch (state) {
      case 'creating_setup_intent':
        return 'Preparing...';
      case 'creating_subscription':
        return 'Creating subscription...';
      case 'confirming':
        return 'Verifying card...';
      case 'updating':
        return 'Joining...';
      default:
        return 'Join Catch';
    }
  };

  // Show Express Checkout for both flows:
  // - create_subscription: needs priceAmount > 0 (subscription mode)
  // - update_payment_method: works with setup mode (no amount needed)
  const shouldShowExpressCheckout =
    showExpressCheckout &&
    onExpressCheckout &&
    (isCreateSubscriptionFlow ? priceAmount && priceAmount > 0 : true);

  return (
    <form onSubmit={onFormSubmit} className="paywall-reset flex w-full flex-col gap-4">
      {/* Express Checkout - Apple Pay / Google Pay for both flows */}
      {shouldShowExpressCheckout && (
        <>
          <StripeExpressCheckoutElement
            priceAmount={isCreateSubscriptionFlow ? priceAmount : undefined}
            onConfirm={onExpressCheckout}
            onReady={() => setShowExpressCheckout(true)}
            isTrial={!isCreateSubscriptionFlow}
          />
          {/* Divider between express checkout and card form */}
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="text-xs text-gray-500">or pay with card</span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>
        </>
      )}

      {/* Stripe Payment Element - reused from payment page */}
      <StripePaymentElement
        onChange={(event) => {
          setIsPaymentComplete(event.complete);
        }}
      />

      {/* Zip Code Input - reused from payment page */}
      <ZipCodeInputV2 form={form} getPrice={() => undefined} />

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting || !isPaymentComplete}
        className="mt-2 w-full rounded-full bg-[#7cf1c7] px-6 py-4 text-base font-bold text-[#0A2540] transition-colors hover:bg-[#6de0b6] disabled:cursor-not-allowed disabled:opacity-70"
      >
        {getButtonText()}
      </button>
    </form>
  );
}
